import { obtenerMensajeError } from "utils/apiErrors";
import { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Checkbox,
  InputLabel,
  Divider,
  Tooltip,
  Backdrop,
  CircularProgress,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiBox from "components/SuiBox";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import { verificarEtapaAnterior } from "../utils/etapaHelpers";

import useUserRoles from "hooks/useUserRoles";
import useAudienciaEditorImages from "./hooks/useAudienciaEditorImages";

import NuevaDeudaForm from "pages/procesos/registrar/components/DeudasForm/components/NuevaDeudaForm/index";
import DatosBasicosApi from "api/datosbasicosApi";
import ProcesosApi from "api/procesosApi";

import ReactQuill from "react-quill";
import styled from "styled-components";
import "react-quill/dist/quill.snow.css";

const StyledEditor = styled(ReactQuill)`
  .ql-toolbar {
    background-color: #f5f5f5;
    border-radius: 8px 8px 0 0;
  }
  .ql-container {
    min-height: 200px;
    font-size: 16px;
    border-radius: 0 0 8px 8px;
    font-family: "Roboto", sans-serif;
  }
  .ql-editor {
    padding: 16px;
    line-height: 1.5;
  }
  .ql-toolbar button:hover {
    background-color: #e0e0e0;
  }
  .ql-editor img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 10px auto;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ContainerConciliacion = ({ proceso, audiencia: initialAudiencia, onAudienciaChange }) => {
  const { user, roles } = useUserRoles();
  const [manifestaciones, setManifestaciones] = useState("");
  const quillManifestacionesRef = useRef(null);
  const [checkedCumplida, setCheckedCumplida] = useState(false);
  const [deudas, setDeudas] = useState([]);
  const [clasesDeuda, setClasesDeuda] = useState([]);
  const [showNewDeudaModal, setShowNewDeudaModal] = useState(false);
  const [audiencia, setAudiencia] = useState(initialAudiencia);
  const [ordenActual] = useState(3);
  const [verificacion, setVerificacion] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Hook personalizado para manejo de imágenes específico de audiencias
  const {
    uploadingImage,
    imageError,
    setupImageHandler,
    processImagesForPDF,
    getQuillModules,
    getQuillFormats,
    validateContent,
    cleanOrphanImages,
    clearError,
    resetImageCounters,
  } = useAudienciaEditorImages(proceso?.identificador, initialAudiencia?.id, "conciliacion");

  useEffect(() => {
    setAudiencia(initialAudiencia);
    fetchDatosIniciales();
    const etapaActual = initialAudiencia?.etapas?.find((etapa) => etapa.orden === ordenActual);
    if (etapaActual?.resultado?.manifestaciones) {
      setManifestaciones(etapaActual.resultado.manifestaciones);
    }
    if (etapaActual?.estado === "CUMPLIDA") setCheckedCumplida(true);
    // Solo verificar etapas si existe audiencia y sus etapas
    if (initialAudiencia && initialAudiencia.etapas) {
      setVerificacion(verificarEtapaAnterior(initialAudiencia.etapas, ordenActual));
    }

    // Resetear contadores de imágenes cuando cambie la audiencia
    resetImageCounters();
  }, [user, proceso, initialAudiencia, resetImageCounters]);

  // Configurar manejador de imagen cuando el ref esté listo
  useEffect(() => {
    const timer = setTimeout(() => {
      setupImageHandler(quillManifestacionesRef, "manifestaciones", (imageInfo) => {
        console.log("Imagen subida en manifestaciones:", imageInfo);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [manifestaciones, setupImageHandler]);

  const fetchDatosIniciales = async () => {
    try {
      setLoading(true);
      const [respDeudas, respClasesDeudas] = await Promise.all([
        ProcesosApi.obtenerDeudasProceso(proceso.id),
        DatosBasicosApi.fetchClasesDeuda(),
      ]);
      setClasesDeuda(respClasesDeudas.data.sort((a, b) => a.id - b.id));
      const deudasFormateadas = respDeudas.data.deudas.map((deuda) => {
        const { formatted: capiFormatted, value: capiValue } = formatCurrency(
          String(deuda.capital || 0)
        );
        const { formatted: interesesFormatted, value: interesesValue } = formatCurrency(
          String(deuda.intereses || 0)
        );
        const { formatted: inteMoraFormatted, value: inteMoraValue } = formatCurrency(
          String(deuda.intereses_mora || 0)
        );
        const { formatted: otrosFormatted, value: otrosValue } = formatCurrency(
          String(deuda.otros_valores || 0)
        );
        const { formatted, value } = formatCurrency(String(deuda.capital_conciliado || 0));
        return {
          ...deuda,
          capital: capiValue,
          capital_formatted: capiFormatted,
          intereses: interesesValue,
          intereses_formatted: interesesFormatted,
          intereses_mora: inteMoraValue,
          intereses_mora_formatted: inteMoraFormatted,
          otros_valores: otrosValue,
          otros_valores_formatted: otrosFormatted,
          capital_conciliado: value,
          capital_conciliado_formatted: formatted,
        };
      });
      setDeudas(deudasFormateadas);
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const number = value ? value.replace(/\D/g, "") : "0";
    const numberValue = Number(number);
    const formatter = new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return {
      formatted: formatter.format(number || 0).replace(/\s/g, ""),
      value: numberValue,
    };
  };

  const handleChange = (field, deudaId) => (event) => {
    const value = event.target.value;
    const index = deudas.findIndex((deuda) => deuda.id === deudaId);
    const nuevasDeudas = [...deudas];
    if (index !== -1) {
      if (field === "etiqueta") {
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          etiqueta: value,
        };
      }
    }
    setDeudas(nuevasDeudas);
  };

  const handleMoneyChange = (field, deudaId) => (event) => {
    const { formatted, value } = formatCurrency(event.target.value);
    const index = deudas.findIndex((deuda) => deuda.id === deudaId);
    const nuevasDeudas = [...deudas];
    if (index !== -1) {
      if (field === "capital_conciliado") {
        // const deudaActual = nuevasDeudas[index];
        // if (value > deudaActual.capital) {
        //   alert("El capital conciliado no puede ser mayor al capital.");
        //   return;
        // }
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          capital_conciliado: value,
          capital_conciliado_formatted: formatted,
        };
      }
      if (field === "intereses") {
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          intereses: value,
          intereses_formatted: formatted,
        };
      }
      if (field === "intereses_mora") {
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          intereses_mora: value,
          intereses_mora_formatted: formatted,
        };
      }
      if (field === "otros_valores") {
        nuevasDeudas[index] = {
          ...nuevasDeudas[index],
          otros_valores: value,
          otros_valores_formatted: formatted,
        };
      }
    }
    setDeudas(nuevasDeudas);
  };

  const handleNewDeudaSubmit = async (deudaData) => {
    try {
      setCreating(true);
      // Obtener las deudas existentes de la misma clase
      const deudasMismaClase = deudas.filter(
        (d) => d.clase_deuda === Number(deudaData.clase_deuda)
      );
      // Calcular el siguiente orden
      const ultimoOrden =
        deudasMismaClase.length > 0 ? Math.max(...deudasMismaClase.map((d) => d.orden ?? 0)) : -1;
      // Añadir el orden a los datos de la deuda
      const deudaConOrden = {
        ...deudaData,
        orden: ultimoOrden + 1,
      };
      await ProcesosApi.crearDeudas(proceso.id, deudaConOrden);
      await fetchDatosIniciales();
      setShowNewDeudaModal(false);
    } catch (error) {
      console.error("Error al crear deuda:", error);
    } finally {
      setCreating(false);
    }
  };

  const cleanHTML = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    return textContent.trim();
  };

  const handleSave = async () => {
    // Limpiar errores de imagen
    clearError();
    if (cleanHTML(manifestaciones) !== "") {
      // Validar contenido
      const manifestacionesValidation = validateContent(manifestaciones);
      if (!manifestacionesValidation.isValid) {
        alert("Error en manifestaciones: " + manifestacionesValidation.errors.join(", "));
        return;
      }
    }
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas guardar la Etapa de Conciliación de Acreencias, esta operacion actualizara todas las Acreencias con la información aquí registrada?`
    );
    if (confirmAction) {
      try {
        setSaving(true);

        // Procesar imágenes para PDF
        const htmlManifestacionesProcesadas = await processImagesForPDF(manifestaciones);

        await Promise.all(
          deudas.map(async (deuda) => {
            await ProcesosApi.actualizarDeuda(proceso.id, deuda.id, deuda);
          })
        );

        const deudasResultado = deudas.map((deuda) => ({
          id_deuda: deuda.id,
          nombre_acreedor: deuda.str_acreedor.nombre_completo,
          etiqueta: deuda.etiqueta,
          capital: deuda.capital,
          suma_total: Math.round(
            (deuda.capital_conciliado || 0) +
              (deuda.intereses || 0) +
              (deuda.intereses_mora || 0) +
              (deuda.otros_valores || 0)
          ),
        }));

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlManifestacionesProcesadas, "text/html");
        const htmlLimpio = doc.body.innerHTML;

        const updatedAudiencia = {
          ...audiencia,
          estado: "REALIZADA",
          etapas: audiencia.etapas.map((etapa) =>
            etapa.orden === ordenActual // CONCILIACION DE ACREENCIAS
              ? {
                  ...etapa,
                  estado: checkedCumplida ? "CUMPLIDA" : "PENDIENTE",
                  resultado: {
                    conciliaciones: deudasResultado,
                    manifestaciones: htmlLimpio,
                  },
                }
              : etapa
          ),
        };

        console.log("Datos guardados: ", updatedAudiencia);
        await ProcesosApi.actualizarAudiencia(audiencia.id, updatedAudiencia);

        // Limpiar imágenes huérfanas después de guardar
        console.log("Iniciando limpieza de imágenes huérfanas después de guardar...");
        try {
          const cleanResult = await cleanOrphanImages({
            manifestaciones: manifestaciones,
          });
          if (cleanResult.success) {
            console.log(
              `Limpieza completada: ${cleanResult.deleted_count || 0} archivos eliminados`
            );
            if (cleanResult.deleted_files && cleanResult.deleted_files.length > 0) {
              console.log("Archivos eliminados:", cleanResult.deleted_files);
            }
          } else {
            console.warn("Error en limpieza de imágenes:", cleanResult.error);
          }
        } catch (cleanError) {
          console.error("Error ejecutando limpieza de imágenes:", cleanError);
        }

        console.log("Audiencia y Acreencias actualizadas correctamente");
        setAudiencia(updatedAudiencia);
        onAudienciaChange && onAudienciaChange(updatedAudiencia);
        await fetchDatosIniciales();

        alert("Etapa guardada exitosamente");
      } catch (error) {
        console.error("Error al actualizar las deudas o la audiencia:", error);
        alert(
          obtenerMensajeError(error, "Error al guardar la etapa. Por favor intenta nuevamente.")
        );
      } finally {
        setSaving(false);
      }
    }
  };

  const handleAbrirEtapa = async () => {
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas abrir la Etapa de Conciliación de Acreencias?`
    );
    if (confirmAction) {
      try {
        setSaving(true);
        const updatedAudiencia = {
          ...audiencia,
          etapas: audiencia.etapas.map((etapa) => {
            if (etapa.orden === ordenActual) {
              return {
                ...etapa,
                estado: "PENDIENTE",
              };
            } else if (etapa.orden > ordenActual) {
              return {
                ...etapa,
                estado: "PROGRAMADA",
              };
            }
            return etapa;
          }),
        };
        await ProcesosApi.actualizarAudiencia(audiencia.id, updatedAudiencia);
        setAudiencia(updatedAudiencia);
        onAudienciaChange && onAudienciaChange(updatedAudiencia);
        fetchDatosIniciales();
      } catch (error) {
        console.error("Error al abrir la Etapa", error);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleEditorManifestacionesChange = useCallback(
    (value) => {
      setManifestaciones(value);
      clearError(); // Limpiar errores cuando el usuario escriba
    },
    [clearError]
  );

  const deudasAgrupadasPorClase = deudas.reduce((acc, deuda) => {
    const clase = deuda.str_clase_deuda.nombre || "Sin Clase";
    if (!acc[clase]) {
      acc[clase] = {
        deudas: [],
        totalCapital: 0,
        totalCapitalConciliado: 0,
      };
    }
    acc[clase].deudas.push(deuda);
    acc[clase].totalCapital += deuda.capital || 0;
    if (!deuda.costas) {
      acc[clase].totalCapitalConciliado += deuda.capital_conciliado || 0;
    }
    return acc;
  }, {});

  const totalGeneralCapital = deudas.reduce((sum, deuda) => sum + (deuda.capital || 0), 0);
  const deudasSinCostas = deudas.filter((deuda) => deuda.costas === false);
  const totalGeneralCapitalConciliado = deudasSinCostas.reduce(
    (sum, deuda) => sum + (deuda.capital_conciliado || 0),
    0
  );

  const isDisabled = () => {
    return (
      !verificacion ||
      audiencia.estado === "SUSPENDIDA" ||
      audiencia.estado === "CERRADA" ||
      (ordenActual > 1 && !verificacion.isQuorumCumplido) ||
      (ordenActual > 1 && !verificacion.isEtapaAnteriorCumplida) ||
      (!verificacion.isEtapaActualPendiente && !verificacion.isEtapaActualProgramada) ||
      (ordenActual === 1 && verificacion.estadoActual !== "CUMPLIDA") ||
      creating ||
      saving ||
      uploadingImage
    );
  };

  const isOpenEtapaDisabled = () => {
    return (
      !verificacion ||
      audiencia.estado === "SUSPENDIDA" ||
      audiencia.estado === "CERRADA" ||
      (ordenActual > 1 && !verificacion.isQuorumCumplido) ||
      (ordenActual > 1 && !verificacion.isEtapaAnteriorCumplida) ||
      verificacion.isEtapaActualPendiente ||
      verificacion.isEtapaActualProgramada ||
      verificacion.isEtapaSiguienteCumplida ||
      creating ||
      saving ||
      uploadingImage
    );
  };

  return (
    <Container>
      {(loading || saving || uploadingImage) && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2, color: "white" }}>
              {uploadingImage ? "Subiendo imagen..." : saving ? "Guardando..." : "Cargando..."}
            </Typography>
          </Box>
        </Backdrop>
      )}

      {imageError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {imageError}
        </Alert>
      )}

      {proceso && audiencia ? (
        <>
          <Grid container spacing={2}>
            <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
              <Table size="small">
                <TableRow sx={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      padding: "4px 8px",
                      textAlign: "center",
                      fontSize: "1.5rem",
                    }}
                  >
                    <span style={{ color: "#eb984e" }}>Total Acreencias</span>
                    <br />
                    <span style={{ color: "#eb984e" }}>
                      {formatCurrency(String(totalGeneralCapital)).formatted}
                    </span>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      padding: "4px 8px",
                      textAlign: "center",
                      fontSize: "1.5rem",
                    }}
                  >
                    <span style={{ color: "#1976d2" }}>Total Conciliado</span>
                    <br />
                    <span style={{ color: "#1976d2" }}>
                      {formatCurrency(String(totalGeneralCapitalConciliado)).formatted}
                    </span>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      padding: "4px 8px",
                      textAlign: "center",
                      fontSize: "1.5rem",
                    }}
                  >
                    <SuiBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <SuiButton
                        variant="gradient"
                        buttonColor="info"
                        onClick={() => setShowNewDeudaModal(true)}
                        disabled={isDisabled()}
                      >
                        Agregar Deuda
                      </SuiButton>
                      <NuevaDeudaForm
                        open={showNewDeudaModal}
                        onClose={() => setShowNewDeudaModal(false)}
                        onSubmit={handleNewDeudaSubmit}
                        disabled={creating}
                        procesoId={proceso.id}
                        clasesDeuda={clasesDeuda}
                      />
                    </SuiBox>
                  </TableCell>
                </TableRow>
              </Table>
            </TableContainer>
          </Grid>
          {Object.entries(deudasAgrupadasPorClase).map(([clase, data]) => {
            const porcConciliadoSobreCapitalConciliado =
              totalGeneralCapitalConciliado > 0
                ? ((data.totalCapitalConciliado / totalGeneralCapitalConciliado) * 100).toFixed(2)
                : "0.00";
            const porcCapitalSobreCapitalTotal =
              totalGeneralCapital > 0
                ? ((data.totalCapital / totalGeneralCapital) * 100).toFixed(2)
                : "0.00";
            return (
              <Box key={clase} sx={{ mt: 1 }}>
                <SuiTypography variant="h5" fontWeight="bold" style={{ color: "#51B0CD" }}>
                  {clase}
                </SuiTypography>
                <Grid container spacing={1}>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableRow>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Acreedor
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Etiqueta
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          <span style={{ color: "#eb984e" }}>Acreencia</span>
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          <span style={{ color: "#eb984e" }}>Coeficiente</span>
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          <span style={{ color: "#1976d2" }}>Conciliado</span>
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          <span style={{ color: "#1976d2" }}>Derecho</span>
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Intereses
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Int. Mora
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Otros Val.
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Total
                        </TableCell>
                      </TableRow>
                      <TableBody>
                        {data.deudas.map((deuda) => {
                          // Calcular la suma total para cada fila
                          const sumaTotal =
                            (deuda.capital_conciliado || 0) +
                            (deuda.intereses || 0) +
                            (deuda.intereses_mora || 0) +
                            (deuda.otros_valores || 0);
                          // Formatear la suma total
                          const { formatted: sumaTotalFormatted } = formatCurrency(
                            String(sumaTotal)
                          );
                          return (
                            <Tooltip
                              key={deuda.id}
                              title={deuda.costas ? "Costas" : null}
                              placement="left"
                            >
                              <TableRow key={deuda.id}>
                                <TableCell sx={{ padding: "2px 2px" }}>
                                  {deuda.str_acreedor.nombre}
                                </TableCell>
                                <TableCell sx={{ padding: "2px 2px" }}>
                                  <SuiInput
                                    value={deuda.etiqueta}
                                    onChange={handleChange("etiqueta", deuda.id)}
                                    fullWidth
                                    disabled={isDisabled()}
                                  />
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                                  <span style={{ color: "#eb984e" }}>
                                    {deuda.capital_formatted}
                                  </span>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                                  <span style={{ color: "#eb984e", fontWeight: "bold" }}>
                                    {((deuda.capital / totalGeneralCapital) * 100).toFixed(2)} %
                                  </span>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                                  <SuiInput
                                    value={deuda.capital_conciliado_formatted}
                                    onChange={handleMoneyChange("capital_conciliado", deuda.id)}
                                    fullWidth
                                    disabled={isDisabled()}
                                  />
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                                  <span style={{ color: "#1976d2", fontWeight: "bold" }}>
                                    {totalGeneralCapitalConciliado > 0 &&
                                    !deuda.costas &&
                                    deuda.capital_conciliado != null
                                      ? (
                                          (deuda.capital_conciliado /
                                            totalGeneralCapitalConciliado) *
                                          100
                                        ).toFixed(2)
                                      : "0.00"}{" "}
                                    %
                                  </span>
                                </TableCell>
                                <TableCell sx={{ padding: "2px 2px" }}>
                                  <SuiInput
                                    value={deuda.intereses_formatted}
                                    onChange={handleMoneyChange("intereses", deuda.id)}
                                    fullWidth
                                    disabled={isDisabled()}
                                  />
                                </TableCell>
                                <TableCell sx={{ padding: "2px 2px" }}>
                                  <SuiInput
                                    value={deuda.intereses_mora_formatted}
                                    onChange={handleMoneyChange("intereses_mora", deuda.id)}
                                    fullWidth
                                    disabled={isDisabled()}
                                  />
                                </TableCell>
                                <TableCell sx={{ padding: "2px 2px" }}>
                                  <SuiInput
                                    value={deuda.otros_valores_formatted}
                                    onChange={handleMoneyChange("otros_valores", deuda.id)}
                                    fullWidth
                                    disabled={isDisabled()}
                                  />
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", padding: "2px 2px" }}>
                                  {sumaTotalFormatted}
                                </TableCell>
                              </TableRow>
                            </Tooltip>
                          );
                        })}
                        <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}>
                            TOTALES
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              padding: "2px 2px",
                              color: "#eb984e",
                            }}
                          >
                            {formatCurrency(String(data.totalCapital)).formatted}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              color: "#eb984e",
                              padding: "2px 2px",
                            }}
                          >
                            {porcCapitalSobreCapitalTotal} %
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              padding: "2px 2px",
                              color: "#1976d2",
                            }}
                          >
                            {formatCurrency(String(data.totalCapitalConciliado)).formatted}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              color: "#1976d2",
                              padding: "2px 2px",
                            }}
                          >
                            {porcConciliadoSobreCapitalConciliado} %
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Divider sx={{ borderColor: "gray", borderStyle: "groove" }} />
              </Box>
            );
          })}
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12} sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <SuiTypography variant="h6" fontWeight="bold">
                Manifestaciones (Desarrollo Audiencia - Punto 2)
              </SuiTypography>
              {uploadingImage && (
                <Typography variant="caption" color="primary">
                  Subiendo imagen...
                </Typography>
              )}
            </Grid>
            <Grid size={12}>
              <StyledEditor
                ref={quillManifestacionesRef}
                value={manifestaciones}
                onChange={handleEditorManifestacionesChange}
                modules={getQuillModules()}
                formats={getQuillFormats()}
                readOnly={isDisabled()}
                placeholder="Ingrese las manifestaciones de la audiencia. Puede insertar imágenes usando el botón de imagen en la barra de herramientas."
              />
            </Grid>
          </Grid>
          {(roles.isAdmin || roles.isOperador) && (
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={8} sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <InputLabel sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={checkedCumplida}
                    disabled={isDisabled()}
                    onChange={(e) => setCheckedCumplida(e.target.checked)}
                    size="small"
                    sx={{
                      color: "white",
                      "&.Mui-checked": {
                        color: "white",
                      },
                      padding: "4px",
                    }}
                  />
                  <SuiTypography variant="h6" fontWeight="light">
                    ¿Cumplida?
                  </SuiTypography>
                </InputLabel>
                <SuiButton
                  variant="gradient"
                  buttonColor="info"
                  onClick={handleSave}
                  disabled={isDisabled()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  Guardar Etapa
                </SuiButton>
                <SuiTypography variant="caption" fontWeight="bold">
                  {!verificacion
                    ? "Cargando verificación..."
                    : audiencia.estado === "SUSPENDIDA"
                    ? "- Audiencia suspendida (no modificable)"
                    : !verificacion.isQuorumCumplido && ordenActual > 1
                    ? "- No se puede avanzar: Quórum no completado"
                    : !verificacion.isEtapaAnteriorCumplida && ordenActual > 1
                    ? "- Complete la etapa anterior primero"
                    : !verificacion.isEtapaActualPendiente && !verificacion.isEtapaActualProgramada
                    ? "- Esta etapa ya fue completada"
                    : ordenActual === 1 && verificacion.estadoActual !== "CUMPLIDA"
                    ? "- Quórum pendiente de completar"
                    : uploadingImage
                    ? "- Subiendo imagen..."
                    : null}
                </SuiTypography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  mt: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <SuiButton
                  variant="gradient"
                  buttonColor="dark"
                  onClick={handleAbrirEtapa}
                  disabled={isOpenEtapaDisabled()}
                >
                  Abrir Etapa
                </SuiButton>
              </Grid>
            </Grid>
          )}
        </>
      ) : (
        <Typography>Cargando...</Typography>
      )}
    </Container>
  );
};

ContainerConciliacion.propTypes = {
  proceso: PropTypes.object,
  audiencia: PropTypes.object,
  onAudienciaChange: PropTypes.func.isRequired,
};

export default ContainerConciliacion;
